import * as XLSX from "xlsx";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { analystConclusion } from "./calc";
import { formatDate, statusLabel, todayISO } from "./api";

export const CREATED_BY = "HM";

function buildReport(state, stats, logs, from, to) {
  const teamName = (id) => state.teams.find((t) => t.id === id)?.name || "-";
  const job = (id) => state.jobs.find((j) => j.id === id);
  const filtered = logs
    .filter((l) => (!from || l.date >= from) && (!to || l.date <= to))
    .sort((a, b) => a.date.localeCompare(b.date) || a.created_at.localeCompare(b.created_at));
  const range = from || to ? `${from ? formatDate(from) : "Start"} – ${to ? formatDate(to) : "Today"}` : "All dates";

  const kpi = [
    ["Total Scope", `${stats.total} items`],
    ["Completed", `${stats.done} / ${stats.total} (${stats.total ? ((stats.done / stats.total) * 100).toFixed(1) : "0.0"}%)`],
    ["In Progress", `${stats.inProgress} items`],
    ["Not Started", `${stats.notStarted} items`],
    ["Weighted Progress", `${stats.overallPct.toFixed(1)}% (${stats.donePoints} / ${stats.totalPoints} pts)`],
  ];
  const teamStatus = stats.perTeam.flatMap((p) =>
    p.items.length
      ? p.items.map((i) => [`${p.team.name}${p.team.specialty ? ` (${p.team.specialty})` : ""}`, p.team.members.join(", "), `${i.job.no}. ${i.job.name}`, i.job.weight, statusLabel(i.status)])
      : [[p.team.name, p.team.members.join(", "), "-", "-", "No assignment"]]);
  const perf = stats.perTeam.map((p) => [p.team.name, p.doneCount, p.pointsDone, p.target, `${p.pct.toFixed(1)}%`, p.verdict]);
  const daily = filtered.map((l) => {
    const j = job(l.job_id);
    return [formatDate(l.date), teamName(l.team_id), j ? `${j.no}. ${j.name}` : "-", statusLabel(l.status), `${l.progress}%`, l.weather, l.notes || "-"];
  });
  return { kpi, teamStatus, perf, daily, range, conclusion: analystConclusion(stats), generated: new Date().toLocaleString("en-GB") };
}

const H = {
  team: ["Team", "Members", "Job", "Weight", "Status"],
  perf: ["Team", "Items Done", "Points Done", "Target Points", "% Productivity", "Performance"],
  daily: ["Date", "Team", "Job", "Status", "Progress", "Weather", "Notes"],
};

const fileName = (ext, from, to) => `inspection-report_${from || "all"}_${to || todayISO()}.${ext}`;

export function exportExcel(state, stats, logs, from, to) {
  const r = buildReport(state, stats, logs, from, to);
  const wb = XLSX.utils.book_new();
  const summary = [
    ["Combined Inspection Progress Report"],
    ["Created by", CREATED_BY],
    ["Generated", r.generated],
    ["Daily log period", r.range],
    [],
    ["KPI", "Value"], ...r.kpi,
    [],
    ["Analyst Conclusion"], [r.conclusion],
  ];
  const add = (rows, name, cols) => {
    const ws = XLSX.utils.aoa_to_sheet(rows);
    ws["!cols"] = cols.map((w) => ({ wch: w }));
    XLSX.utils.book_append_sheet(wb, ws, name);
  };
  add(summary, "Summary", [28, 60]);
  add([H.team, ...r.teamStatus], "Status per Team", [22, 26, 32, 8, 14]);
  add([H.perf, ...r.perf], "Performance", [16, 12, 12, 14, 15, 18]);
  add([[`Daily Log – ${r.range}`], H.daily, ...(r.daily.length ? r.daily : [["No daily logs in this period"]])], "Daily Log", [14, 12, 30, 13, 10, 12, 50]);
  XLSX.writeFile(wb, fileName("xlsx", from, to));
  return r.daily.length;
}

export function exportPdf(state, stats, logs, from, to) {
  const r = buildReport(state, stats, logs, from, to);
  const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
  const W = doc.internal.pageSize.getWidth();
  const M = 40;
  const amber = [245, 158, 11];
  const head = { fillColor: [17, 24, 39], textColor: [255, 255, 255], fontStyle: "bold" };
  const base = { styles: { fontSize: 9, cellPadding: 5 }, headStyles: head, alternateRowStyles: { fillColor: [245, 247, 250] }, margin: { left: M, right: M } };

  doc.setFillColor(...amber); doc.rect(0, 0, W, 6, "F");
  doc.setFont("helvetica", "bold"); doc.setFontSize(20); doc.setTextColor(17, 24, 39);
  doc.text("Combined Inspection Progress Report", M, 44);
  doc.setFont("helvetica", "normal"); doc.setFontSize(10); doc.setTextColor(90);
  doc.text(`Created by ${CREATED_BY}   ·   Generated ${r.generated}   ·   Daily log period: ${r.range}`, M, 62);

  const title = (t, y) => { doc.setFont("helvetica", "bold"); doc.setFontSize(12); doc.setTextColor(17, 24, 39); doc.text(t, M, y); return y + 8; };
  const next = () => doc.lastAutoTable.finalY + 26;

  autoTable(doc, { ...base, startY: title("Key Indicators", 88), head: [["KPI", "Value"]], body: r.kpi, tableWidth: 380 });
  autoTable(doc, { ...base, startY: title("Performance & Workload Analysis", next()), head: [H.perf], body: r.perf });

  let y = next();
  y = title("Analyst Conclusion", y);
  doc.setFont("helvetica", "normal"); doc.setFontSize(10); doc.setTextColor(40);
  const lines = doc.splitTextToSize(r.conclusion, W - M * 2);
  if (y + lines.length * 13 > doc.internal.pageSize.getHeight() - 40) { doc.addPage(); y = title("Analyst Conclusion", 50); }
  doc.setDrawColor(...amber); doc.setLineWidth(3); doc.line(M, y + 2, M, y + 6 + lines.length * 13);
  doc.text(lines, M + 10, y + 12);

  doc.addPage();
  autoTable(doc, { ...base, startY: title("Status per Team", 50), head: [H.team], body: r.teamStatus });
  autoTable(doc, {
    ...base, startY: title(`Daily Log (${r.range})`, next()), head: [H.daily],
    body: r.daily.length ? r.daily : [[{ content: "No daily logs in this period", colSpan: 7, styles: { halign: "center", textColor: 120 } }]],
    columnStyles: { 6: { cellWidth: 230 } },
  });

  const pages = doc.getNumberOfPages();
  for (let i = 1; i <= pages; i++) {
    doc.setPage(i); doc.setFontSize(8); doc.setTextColor(140);
    doc.text(`Combined Inspection Progress · Created by ${CREATED_BY}`, M, doc.internal.pageSize.getHeight() - 18);
    doc.text(`Page ${i} of ${pages}`, W - M, doc.internal.pageSize.getHeight() - 18, { align: "right" });
  }
  doc.save(fileName("pdf", from, to));
  return r.daily.length;
}
